import React, { useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Tooltip from "react-tooltip";
import { useQueryClient } from "@tanstack/react-query";
import styles from "../scss/AdditionalClinicSurgesModal.module.scss";
import {
  applicationLevelOptions,
  cascadingOptions,
  exclusionsOptions,
  getInitialValues,
  rulesOptions,
  taxTypeOptions,
} from "../AdditionalClinicSurgesModal.utils.js";
import { Button } from "../../../../../../../shared/Button/Button.js";
import { Input } from "../../../../../../../shared/Input/Input.js";
import { Select } from "../../../../../../../shared/Select/Select.js";
import { Modal } from "../../../../../../../shared/Modal/Modal.js";
import { InputLabel } from "../../../../../../../shared/InputLabel/InputLabel.js";
import { useAddAdditionalClinicSurges } from "../hooks/useAddAdditionalClinicSurges.js";
import { uiNotification } from "../../../../../../../services/UINotificationService.js";
import { CircularProgress } from "../../../../../../../shared/CircularProgress/CircularProgress.js";
import { useEditAdditionalClinicSurges } from "../hooks/useEditAdditionalClinicSurges.js";
import { InputError } from "../../../../../../../shared/InputError/InputError.js";
import { InfoIcon } from "../../../../../../../assets/Icons/InfoIcon.js";

const EditClinicAdditionalSurgesModal = ({ clinicId, modal, setModal }) => {
  const { mutate: addTax, isLoading } = useAddAdditionalClinicSurges(clinicId);
  const { mutate: editTax, isLoading: isEditing } =
    useEditAdditionalClinicSurges({ clinicId, id: modal.data?.id });
  const queryClient = useQueryClient();

  const schema = yup.object().shape({
    name: yup.string().required(),
    percentage: yup
      .number("Percentage must be a number")
      .required("Percentage is required")
      .moreThan(0, "Percentage must be greater than 0")
      .max(100, "Percentage cannot exceed 100")
      .test(
        "max-3-decimals",
        "Maximum 3 decimal allowed",
        (value) =>
          value === undefined || /^\d+(\.\d{1,3})?$/.test(value.toString()),
      ),
    applicationLevel: yup.object().required(),
    taxType: yup.object().required(),
    rules: yup.object().required(),
  });

  const closeModal = () => setModal({ show: false, data: null });

  const handleMutationSuccess = (message) => {
    closeModal();
    uiNotification.success(message);
    queryClient.invalidateQueries(["additionalClinicSurges"]);
  };

  const mutateTax = (dto) => {
    const mutation = modal.data ? editTax : addTax;
    const successMessage = modal.data
      ? "Additional Clinic Surcharge Updated Successfully"
      : "Additional Clinic Surcharge Created Successfully";

    mutation(dto, {
      onSuccess: () => handleMutationSuccess(successMessage),
      onError: (error) => uiNotification.error(error.response.data.message),
    });
  };

  const { values, errors, setFieldValue, handleSubmit } = useFormik({
    validationSchema: schema,
    initialValues: getInitialValues(modal),
    validateOnChange: false,
    validateOnBlur: false,

    onSubmit: (values) => {
      const exclusions = values.exclusions.map((option) => option.value);
      const dto = {
        label: values.name,
        percentage: values.percentage,
        level: values.applicationLevel.value,
        tax_type: values.taxType.value,
        cascading: values.cascading.value,
        type: values.rules.value,
        exclusions,
      };

      mutateTax(dto);
    },
  });

  const handleInputChange = (event) => {
    setFieldValue(event.target.name, event.target.value);
  };

  const pretaxOrPostaxSelected = useMemo(() => {
    return (
      values.taxType.value === "pre_tax" || values.taxType.value === "post_tax"
    );
  }, [values.taxType]);

  useEffect(() => {
    if (!pretaxOrPostaxSelected) {
      setFieldValue("cascading", cascadingOptions[0]);
    }

    if (values.applicationLevel.value === "item") {
      setFieldValue("exclusions", []);
    }
  }, [values.taxType, values.applicationLevel]);

  return (
    <Modal
      isOpen
      className={styles.additionalSurgesModal}
      onClose={closeModal}
      header={
        <Modal.Title>Create Additional Clinic Surcharges & Taxes</Modal.Title>
      }
      footer={
        <div className={styles.footerContainer}>
          <Button
            variant="outlined"
            onClick={closeModal}
            isDisabled={isLoading || isEditing}
          >
            Back
          </Button>
          <Button
            onClick={handleSubmit}
            leftAdornment={
              isLoading || isEditing ? (
                <CircularProgress size="tiny" color="white" />
              ) : null
            }
            isDisabled={isLoading || isEditing}
          >
            Save
          </Button>
        </div>
      }
      footerNoBorder
    >
      <div className={styles.modalContent}>
        <div>
          <InputLabel>Name</InputLabel>
          <Input
            type="text"
            name="name"
            size="small"
            placeholder="Enter Name"
            className={styles.nameInput}
            value={values.name}
            onChange={handleInputChange}
            isError={Boolean(errors.name)}
          ></Input>
        </div>
        <div>
          <InputLabel>%</InputLabel>
          <Input
            type="text"
            name="percentage"
            size="small"
            value={values.percentage}
            onChange={(event) =>
              setFieldValue("percentage", event.target.value)
            }
            isError={Boolean(errors.percentage)}
          ></Input>
          {errors.percentage && <InputError>{errors.percentage}</InputError>}
        </div>
        <div>
          <InputLabel>Application Level</InputLabel>
          <Select
            size="small"
            value={values.applicationLevel}
            onChange={(option) => setFieldValue("applicationLevel", option)}
            options={applicationLevelOptions}
            isError={Boolean(errors.applicationLevel)}
          />
        </div>
        <div>
          <InputLabel>Tax Type</InputLabel>
          <Select
            size="small"
            value={values.taxType}
            onChange={(option) => setFieldValue("taxType", option)}
            options={taxTypeOptions}
            isError={Boolean(errors.taxType)}
          />
        </div>
        {pretaxOrPostaxSelected && (
          <div>
            <InputLabel>Cascading</InputLabel>
            <Select
              size="small"
              value={values.cascading}
              onChange={(option) => setFieldValue("cascading", option)}
              options={cascadingOptions}
            />
          </div>
        )}
        <div>
          <InputLabel>Rules</InputLabel>
          <Select
            size="small"
            value={values.rules}
            onChange={(option) => setFieldValue("rules", option)}
            options={rulesOptions}
            isError={Boolean(errors.rules)}
          />
        </div>
        <div>
          <InputLabel className={styles.exclusionsLabel}>
            Exclusions{" "}
            {values.applicationLevel.value === "item" && (
              <>
                <span data-tip data-for="infoToolTip">
                  <InfoIcon width={"15px"} />
                </span>{" "}
                <Tooltip id="infoToolTip" aria-haspopup="true">
                  Please note that when item level is selected, additional
                  surcharges are automatically excluded from non-taxable items
                  (e.g. gift cards, memberships, service deposits, treatment
                  plan and cancellation fees)
                </Tooltip>
              </>
            )}
          </InputLabel>
          <Select
            size="small"
            value={values.exclusions}
            onChange={(option) => setFieldValue("exclusions", option)}
            options={exclusionsOptions}
            isMulti
            isDisabled={values.applicationLevel.value === "item"}
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditClinicAdditionalSurgesModal;
